/**
 * Averigua desde dónde se está abriendo el formulario público.
 *
 * El formulario vive en app.sontickets.com pero se usa embebido en la web de
 * cada cliente (por ejemplo noi.work). Para saber si un acceso es legítimo hay
 * que averiguar el dominio de la página que lo embebe, y eso no es infalible:
 *
 *  · `location.ancestorOrigins` es lo más fiable, pero **Firefox no lo tiene**.
 *  · `document.referrer` funciona como respaldo, pero el sitio que embebe puede
 *    ocultarlo con su Referrer-Policy, y algunas extensiones lo eliminan.
 *
 * Por eso esta información sirve para **registrar y decidir**, nunca para
 * bloquear a ciegas: un falso negativo dejaría a un cliente sin poder reservar.
 */

export type ModoSeguridad = 'off' | 'monitor' | 'enforce';

export type OrigenAcceso = {
  /** El formulario se está mostrando dentro de un iframe. */
  enIframe: boolean;
  /** Dominio de la página que lo embebe, ya normalizado. `null` si no se pudo saber. */
  dominioPadre: string | null;
  /** Cómo se averiguó, útil para diagnosticar. */
  fuente: 'ancestorOrigins' | 'referrer' | 'ninguna';
  referrer: string;
};

/** `https://www.NOI.work:443/algo` → `noi.work` */
export const normalizarDominio = (valor: string): string | null => {
  if (!valor) return null;
  try {
    const url = valor.includes('://') ? new URL(valor) : new URL(`https://${valor}`);
    return url.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
};

/**
 * ¿La URL traía un código de reserva cuando se abrió la página?
 *
 * Se mira **una sola vez, al cargar la aplicación**, y es a propósito: en
 * cuanto la reserva se carga, la aplicación reescribe la URL y borra el
 * código, así que preguntarlo más tarde siempre respondería que no.
 *
 * Sirve para distinguir dos caminos que son distintos de verdad: quien llega
 * pulsando el enlace de su correo, y quien entra a la web del cliente y teclea
 * allí su código. Ambos acaban en la misma pantalla.
 */
export const llegoConCodigo: boolean = (() => {
  try {
    if (typeof window === 'undefined') return false;
    if (new URLSearchParams(window.location.search).get('code')) return true;
    // Enlace alternativo, con el código en la ruta: /reservations/{id}/{code}
    return /\/reservations\/[^/]+\/[^/]+/.test(window.location.pathname);
  } catch {
    return false;
  }
})();

export const detectarOrigen = (): OrigenAcceso => {
  let enIframe = false;
  try {
    enIframe = window.self !== window.top;
  } catch {
    // Un error de acceso entre orígenes ya significa que estamos embebidos.
    enIframe = true;
  }

  const referrer = typeof document !== 'undefined' ? document.referrer : '';

  try {
    const ancestros = window.location.ancestorOrigins;
    if (ancestros && ancestros.length > 0) {
      const dominio = normalizarDominio(ancestros[0]);
      if (dominio) return { enIframe, dominioPadre: dominio, fuente: 'ancestorOrigins', referrer };
    }
  } catch {
    /* no soportado (Firefox): se usa el referrer */
  }

  const porReferrer = normalizarDominio(referrer);
  if (porReferrer) return { enIframe, dominioPadre: porReferrer, fuente: 'referrer', referrer };

  return { enIframe, dominioPadre: null, fuente: 'ninguna', referrer };
};

/** Coincide el dominio exacto o cualquier subdominio suyo. */
export const dominioPermitido = (dominio: string | null, permitidos: string[]): boolean => {
  if (!dominio || permitidos.length === 0) return false;
  return permitidos.some((entrada) => {
    const permitido = normalizarDominio(entrada);
    if (!permitido) return false;
    return dominio === permitido || dominio.endsWith(`.${permitido}`);
  });
};

export type ResultadoAcceso = {
  permitido: boolean;
  motivo:
    | 'dominio-permitido'
    | 'mismo-sitio'
    | 'sesion-iniciada'
    | 'codigo-de-reserva'
    | 'sin-restriccion'
    | 'solo-observando'
    | 'origen-no-permitido';
  origen: OrigenAcceso;
};

type EvaluarParams = {
  modo: ModoSeguridad;
  dominiosPermitidos: string[];
  /** Hay una sesión abierta en el panel. */
  conSesion: boolean;
  /** La URL trae un código de reserva: viene del correo de confirmación. */
  conCodigo: boolean;
};

/**
 * Decide si se puede usar el formulario. Ante la duda, permite: es preferible
 * un acceso de más que un cliente legítimo que no puede reservar.
 */
export const evaluarAcceso = ({
  modo,
  dominiosPermitidos,
  conSesion,
  conCodigo,
}: EvaluarParams): ResultadoAcceso => {
  const origen = detectarOrigen();

  if (modo === 'off') return { permitido: true, motivo: 'sin-restriccion', origen };

  // Quien ya entró al panel puede crear reservas desde ahí.
  if (conSesion) return { permitido: true, motivo: 'sesion-iniciada', origen };

  // Quien llega desde el correo para ver, cambiar o cancelar su reserva.
  if (conCodigo) return { permitido: true, motivo: 'codigo-de-reserva', origen };

  // Abierto directamente en app.sontickets.com, sin estar embebido.
  if (!origen.enIframe && !origen.dominioPadre) {
    return { permitido: modo !== 'enforce', motivo: 'mismo-sitio', origen };
  }

  if (dominioPermitido(origen.dominioPadre, dominiosPermitidos)) {
    return { permitido: true, motivo: 'dominio-permitido', origen };
  }

  // En observación se deja pasar y solo se registra, para poder ver el tráfico
  // real antes de bloquear a nadie.
  if (modo === 'monitor') return { permitido: true, motivo: 'solo-observando', origen };

  return { permitido: false, motivo: 'origen-no-permitido', origen };
};
